import React, { useState } from "react";

import { stepCompleted } from "EmployeeApplication/actions";
import { getDependents, getEmployeeApplication, getEmployeeForm, useUpdateEmployeePolicyMutation } from "EmployeeApplication/api";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import formatCurrency from "Utils/formatCurrency";
import { formatNameFL } from "Utils/formatName";

import Button from "Cobalt/Button";
import Header from "Cobalt/Header";
import { Table, HeaderTR, TH, TD, TR } from "Cobalt/TableV2";
import InfoPopover from "Common/InfoPopover";

import { VariableTermLifeEnrollOrDeclineButton } from "./TermLife/termLife.modals";

const ConfirmEnrollmentButton = ({
  plan,
  planCost,
  selectedEmployeeAmount,
  selectedSpouseAmount,
  employeeCostAmount,
  spouseCostAmount,
  selectedChildAmount,
  childCostAmount,
}) => {
  const { id: employeePolicyID } = useSelector(getEmployeeApplication);
  const dependents = useSelector(getDependents);
  const dispatch = useDispatch();
  const [updateEmployeePolicy] = useUpdateEmployeePolicyMutation();
  const [submitting, setSubmitting] = useState(false);

  const confirmEnrollment = async () => {
    setSubmitting(true);
    const dependentIDs = dependents.map((dependent) => dependent.id);
    const planType = plan.type;
    const isEnrollingType = _.camelCase(`isEnrolling_${planType}`);
    const typeCoverageAmount = _.camelCase(`${planType}_CoverageAmount`);
    const enrolledTypePlanID = `${_.camelCase(`enrolled_${planType}_Plan`)}ID`;
    const dependentsEnrollingType = _.camelCase(`dependentsEnrolling_${planType}`);
    const chooseTypeLifePlan = _.camelCase(`choose_${planType}_Plan`);
    const formData = { id: employeePolicyID };
    formData[typeCoverageAmount] = selectedEmployeeAmount;

    if (formData[typeCoverageAmount]) {
      formData[isEnrollingType] = "Yes";
      formData[enrolledTypePlanID] = plan.id;
      formData[dependentsEnrollingType] = dependentIDs.reduce((acc, id) => ({ ...acc, [id]: false }), {});
      if (selectedSpouseAmount) {
        formData[`${_.camelCase(`${planType}_SpouseCoverageAmount`)}`] = selectedSpouseAmount;
      } else {
        formData[`${_.camelCase(`${planType}_SpouseCoverageAmount`)}`] = null;
      }
    } else {
      formData[isEnrollingType] = "No";
      formData[enrolledTypePlanID] = null;
      formData[dependentsEnrollingType] = null;
    }

    const { data } = await updateEmployeePolicy(formData);
    if (data) {
      dispatch(stepCompleted(chooseTypeLifePlan));
      return;
    }
    setSubmitting(false);
  };

  if (selectedEmployeeAmount && selectedEmployeeAmount > 0) {
    return (
      <VariableTermLifeEnrollOrDeclineButton
        plan={plan}
        planCost={planCost}
        selectedEmployeeAmount={selectedEmployeeAmount}
        employeeCostAmount={employeeCostAmount}
        selectedSpouseAmount={selectedSpouseAmount}
        spouseCostAmount={spouseCostAmount}
        selectedChildAmount={selectedChildAmount}
        childCostAmount={childCostAmount}
      />
    );
  }
  return (
    <Button onClick={confirmEnrollment} disabled={submitting}>
      Continue
    </Button>
  );
};

const LifeVariableAmountSelection = ({ plan, planCost, coverageAmount, spouseCoverageAmount }) => {
  const employeeForm = useSelector(getEmployeeForm);
  const dependents = useSelector(getDependents) || [];
  const costPerDollar = planCost.costPerThousand / 1000;

  const [selectedAmount, setSelectedAmount] = useState(coverageAmount);
  const [costAmount, setCostAmount] = useState(selectedAmount && selectedAmount * costPerDollar);

  const [selectedSpouseAmount, setSelectedSpouseAmount] = useState(spouseCoverageAmount);
  const [spouseCostAmount, setSpouseCostAmount] = useState(spouseCoverageAmount && spouseCoverageAmount * costPerDollar);

  // Calculate employee coverage max
  let coverageMax;
  if (employeeForm.annualSalary && plan.max_salary_multiplier) {
    const salaryMax = employeeForm.annualSalary * plan.max_salary_multiplier;
    const closestMaxToSalaryMax = Math.floor(salaryMax / plan.variable_coverage_amount_increment) * plan.variable_coverage_amount_increment;
    coverageMax = Math.min(closestMaxToSalaryMax, plan.variable_coverage_amount_max);
  } else {
    coverageMax = plan.variable_coverage_amount_max;
  }

  const setCosts = (amount) => {
    setSelectedAmount(amount);
    setCostAmount(amount ? amount * costPerDollar : 0);
  };

  const setSpouseCosts = (amount) => {
    setSelectedSpouseAmount(amount);
    setSpouseCostAmount(amount ? amount * costPerDollar : 0);
  };

  const spouse = dependents.find((dependent) => dependent.dependentType === "spouse" || dependent.dependentType === "domestic_partner");
  const child = dependents.find((dependent) => dependent.dependentType === "child");
  const selectedChildAmount = child ? planCost?.childCoverageAmount : null;
  const childCostAmount = child ? planCost?.costBreakdowns?.eeChildren?.dependentCost : null;

  return (
    <Table>
      <thead>
        <HeaderTR>
          <TH width="50%">Person</TH>
          <TH align="left">Coverage Amount</TH>
          <TH>
            Your Monthly Cost
            {plan.contribution_strategy === "Contributory" && (
              <InfoPopover
                infoTitle="Payroll Contributions"
                infoText="This is the amount you pay each month, taken directly out of your paycheck."
                placement="top"
              />
            )}
          </TH>
        </HeaderTR>
      </thead>
      <tbody>
        {/* Employee row */}
        <TR highlightFirstCell={false} withBorder={false}>
          <TD id="coverage-amount-slider-label">{formatNameFL(employeeForm)}</TD>
          <TD align="left">
            <Header level={5} weight="bold">
              {selectedAmount ? formatCurrency(selectedAmount) : "No Coverage"}
            </Header>
          </TD>
          <TD>
            <Header level={5} weight="bold">
              {formatCurrency(costAmount, 2)}
            </Header>
          </TD>
        </TR>
        <TR withBorder={false}>
          <TD />
          <TD colSpan={2}>
            <input
              aria-labelledby="coverage-amount-slider-label"
              type="range"
              min={0}
              max={coverageMax}
              step={plan.variable_coverage_amount_increment}
              onChange={(e) => setCosts(parseInt(e.target.value, 10))}
              value={selectedAmount || 0}
            />
          </TD>
        </TR>

        {spouse && plan.spouse_coverage && (
          <>
            {/* Spouse row */}
            {plan.spouse_variable_coverage ? (
              <>
                <TR highlightFirstCell={false} withBorder={false}>
                  <TD id="spouse-coverage-slider-label">{formatNameFL(spouse)}</TD>
                  <TD align="left">
                    <Header level={5} weight="bold">
                      {selectedSpouseAmount ? formatCurrency(selectedSpouseAmount) : "No Coverage"}
                    </Header>
                  </TD>
                  <TD>
                    <Header level={5} weight="bold">
                      {formatCurrency(spouseCostAmount, 2)}
                    </Header>
                  </TD>
                </TR>
                <TR withBorder={false}>
                  <TD />
                  <TD colSpan={2}>
                    <input
                      aria-labelledby="spouse-coverage-slider-label"
                      type="range"
                      min={0}
                      max={plan.spouse_variable_coverage_amount_max}
                      step={plan.spouse_variable_coverage_amount_increment}
                      onChange={(e) => setSpouseCosts(parseInt(e.target.value, 10))}
                      value={selectedSpouseAmount || 0}
                    />
                  </TD>
                </TR>
              </>
            ) : (
              <TR highlightFirstCell={false} withBorder={false}>
                <TD>{formatNameFL(spouse)}</TD>
                <TD align="left">
                  <Header level={5} weight="bold">
                    {plan.spouse_coverage_amount ? formatCurrency(plan.spouse_coverage_amount) : "No Coverage"}
                  </Header>
                </TD>
                <TD>
                  <Header level={5} weight="bold">
                    {formatCurrency(spouseCostAmount, 2)}
                  </Header>
                </TD>
              </TR>
            )}
          </>
        )}
        <TR withBorder={false}>
          <TD />
          <TD>&nbsp;</TD>
          <TD>
            <ConfirmEnrollmentButton
              plan={plan}
              planCost={planCost}
              selectedEmployeeAmount={selectedAmount}
              selectedSpouseAmount={selectedSpouseAmount}
              employeeCostAmount={costAmount}
              spouseCostAmount={spouseCostAmount}
              childCoverageAmount={selectedChildAmount}
              childCostAmount={childCostAmount}
            />
          </TD>
        </TR>
      </tbody>
    </Table>
  );
};
export default LifeVariableAmountSelection;
